import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { openOAuthPopup } from '../utils/AuthUtils';
import { config } from '../Constants';

const URL = config.url;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authToken, setAuthToken] = useState(() => {
        const token = localStorage.getItem('authToken');
        return token;
    });

    const logout = useCallback(async () => {
        try {
            await fetch(`${URL}/api/v1/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': authToken ? `Bearer ${authToken}` : '',
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('authToken');
        setAuthToken(null);
        setUser(null);
    }, [authToken]);

    const validateToken = useCallback(async () => {
        if (!authToken) {
            setLoading(false);
            return false;
        }

        try {
            const response = await fetch(`${URL}/api/v1/auth/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();   
                if (data.result === 'SUCCESS') {
                    setUser(data.data);
                    setLoading(false);
                    return true;
                }
            }
            
            throw new Error('Token validation failed');
        } catch (error) {
            localStorage.removeItem('authToken');
            setAuthToken(null);
            setUser(null);
            setLoading(false);
            return false;
        }
    }, [authToken]);

    // Method to directly set token and validate user
    const loginSuccess = useCallback(async (token) => {
        if (!token) {
            console.error("No token provided to loginSuccess");
            return false;
        }
        
        localStorage.setItem('authToken', token);
        setAuthToken(token);
        return await validateToken();
    }, [validateToken]);

    // Initialize authentication on component mount
    useEffect(() => {
        validateToken();
    }, [validateToken]);

    // Refresh user data periodically (every 5 minutes) to keep subscription status updated
    useEffect(() => {
        if (user) {
            const interval = setInterval(() => {
                validateToken();
            }, 5 * 60 * 1000); // Every 5 minutes
            
            return () => clearInterval(interval);
        }
    }, [user, validateToken]);

    // Handle login with optional redirect
    const login = useCallback((redirectPath) => {
        // Pass the loginSuccess callback to the popup function
        openOAuthPopup(loginSuccess, redirectPath);
    }, [loginSuccess]);

    // Determine if user has active premium based on subscription status
    const isPremiumStatus = !!user && user.premium && user.subscriptionStatus !== 'canceled';

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        user,
        authToken,
        isAuthenticated: !!user,
        isPremium: isPremiumStatus,
        loading,
        login,
        loginSuccess,
        logout,
        validateToken
    }), [user, authToken, isPremiumStatus, loading, login, loginSuccess, logout, validateToken]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};